import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import PlayerSelector from "../components/Players/PlayerSelector"; // Pfad zu PlayerSelector
import {
  fetchAllowedContent,
  fetchFeeds,
  startFeed,
  createFeed,
  updateFeedContents,
  renameFeed,
  deleteFeed,
} from "../api/api";
import {
  Box,
  Button,
  Typography,
  Paper,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import { useTranslation } from "react-i18next";

type Feed = {
  id: string;
  external_name: string;
  content_ids: string[];
};

type Content = {
  file_name: string;
  content_name: string;
  mediatype?: string;
};

type FeedsProps = {
  adminPlayer?: string;
};

const Feeds: React.FC<FeedsProps> = ({ adminPlayer }) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [selectedPlayer, setSelectedPlayer] = useState<string | null>(
    adminPlayer || null
  );
  const [feeds, setFeeds] = useState<Feed[]>([]);
  const [selectedFeedId, setSelectedFeedId] = useState<string | null>(null);
  const [selectedFeedName, setSelectedFeedName] = useState<string | null>(null);

  const [allContent, setAllContent] = useState<Content[]>([]);
  const [available, setAvailable] = useState<Content[]>([]);
  const [feedContents, setFeedContents] = useState<Content[]>([]);
  const [snackbarMsg, setSnackbarMsg] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<"create" | "rename">("create");
  const [feedName, setFeedName] = useState<string>("");
  const [renameFeedId, setRenameFeedId] = useState<string | null>(null);

  // Hilfsfunktion zum Sortieren der Feeds
  const sortFeeds = (feedsArray: Feed[]) => {
    return [...feedsArray].sort((a, b) =>
      a.external_name.localeCompare(b.external_name)
    );
  };

  // Spielerwechsel setzt alles zurück
  useEffect(() => {
    console.log("--- useEffect [adminPlayer] triggered ---");
    console.log("Current adminPlayer:", adminPlayer);
    if (adminPlayer) {
      setSelectedPlayer(adminPlayer);
    } else {
      setSelectedPlayer(null);
    }
  }, [adminPlayer]);

  // Feeds laden
  useEffect(() => {
    const loadFeeds = async () => {
      console.log("--- useEffect [selectedPlayer] (loadFeeds) triggered ---");
      console.log("Current selectedPlayer for fetching feeds:", selectedPlayer);

      if (selectedPlayer) {
        try {
          const fetchedFeeds = await fetchFeeds(selectedPlayer);
          const sortedFeeds = sortFeeds(fetchedFeeds);
          setFeeds(sortedFeeds);
          console.log("Fetched and sorted feeds:", sortedFeeds);

          let newSelectedFeedId: string | null = null;
          let newSelectedFeedName: string | null = null;

          if (sortedFeeds.length > 0) {
            const currentSelected = sortedFeeds.find(
              (f: Feed) => f.id === selectedFeedId
            );
            if (currentSelected) {
              newSelectedFeedId = currentSelected.id;
              newSelectedFeedName = currentSelected.external_name;
            } else {
              newSelectedFeedId = sortedFeeds[0].id;
              newSelectedFeedName = sortedFeeds[0].external_name;
            }
          }
          setSelectedFeedId(newSelectedFeedId);
          setSelectedFeedName(newSelectedFeedName);
          console.log("New selectedFeedId after fetch:", newSelectedFeedId);
          console.log("New selectedFeedName after fetch:", newSelectedFeedName);
        } catch (error) {
          console.error("Error fetching feeds:", error);
          setSnackbarMsg(t("feeds.snackbar.errorLoadingFeeds"));
        }
      } else {
        setFeeds([]);
        setSelectedFeedId(null);
        setSelectedFeedName(null);
        console.log("selectedPlayer is null, resetting feeds/selection.");
      }
    };
    loadFeeds();
  }, [selectedPlayer, selectedFeedId, t]);

  // Content laden
  useEffect(() => {
    const loadAllContent = async () => {
      console.log(
        "--- useEffect [selectedPlayer] (loadAllContent) triggered ---"
      );
      console.log("Current selectedPlayer for fetching content:", selectedPlayer);
      if (selectedPlayer) {
        try {
          const resp = await fetchAllowedContent(selectedPlayer);
          const all: Content[] = Array.isArray(resp) ? resp : resp.allowed || [];
          setAllContent(all);
          console.log("Fetched allContent:", all);
        } catch (error) {
          console.error("Error fetching allowed content:", error);
          setSnackbarMsg(t("feeds.snackbar.errorLoadingContent"));
        }
      } else {
        setAllContent([]);
        console.log("selectedPlayer is null, resetting allContent.");
      }
    };
    loadAllContent();
  }, [selectedPlayer, t]);

  // Feed-Inhalte/Verfügbare berechnen
  useEffect(() => {
    console.log("--- useEffect [selectedFeedId, feeds, allContent] triggered ---");
    console.log("Current selectedFeedId:", selectedFeedId);
    console.log("Current feeds (length):", feeds.length);
    console.log("Current allContent (length):", allContent.length);

    if (!selectedFeedId || feeds.length === 0 || allContent.length === 0) {
      setFeedContents([]);
      setAvailable([]);
      console.log("Conditions not met for calculating feed contents/available.");
      return;
    }

    const feed = feeds.find((f: Feed) => f.id === selectedFeedId);
    console.log("Found feed:", feed);

    if (feed) {
      setSelectedFeedName(feed.external_name);
      console.log("Set selectedFeedName to:", feed.external_name);

      const feedIds = feed.content_ids ?? [];
      console.log("Feed content_ids:", feedIds);

      const sortedFeedContents: Content[] = [];
      feedIds.forEach((id: string) => {
        const contentItem = allContent.find((c: Content) => c.file_name === id);
        if (contentItem) {
          sortedFeedContents.push(contentItem);
        } else {
          console.warn(
            `Content item with file_name ${id} (from feed.content_ids) not found in allContent.`
          );
        }
      });
      setFeedContents(sortedFeedContents);
      console.log("Calculated feedContents:", sortedFeedContents);

      const currentAvailable = allContent.filter(
        (c: Content) => !feedIds.includes(c.file_name)
      );
      setAvailable(currentAvailable);
      console.log("Calculated available content:", currentAvailable);
    } else {
      setSelectedFeedId(null);
      setSelectedFeedName(null);
      setFeedContents([]);
      setAvailable([]);
      console.warn("Selected feed not found in current feeds list. Resetting selection.");
    }
  }, [selectedFeedId, feeds, allContent]);

  const updateFeedContentsState = useCallback(
    async (feedIdToUpdate: string, newFeedContentFileNames: string[]) => {
      console.log("--- updateFeedContentsState triggered ---");
      console.log(
        "Updating feed:",
        feedIdToUpdate,
        "with content IDs:",
        newFeedContentFileNames
      );
      if (selectedPlayer && feedIdToUpdate) {
        try {
          await updateFeedContents(
            selectedPlayer,
            feedIdToUpdate,
            newFeedContentFileNames
          );
          setSnackbarMsg(t("feeds.snackbar.feedUpdated"));

          const updatedFeeds = await fetchFeeds(selectedPlayer);
          const sortedFeeds = sortFeeds(updatedFeeds);
          setFeeds(sortedFeeds);
          console.log("Feeds re-fetched and sorted after update.");
        } catch (error) {
          console.error("Fehler beim Aktualisieren des Feeds:", error);
          setSnackbarMsg(t("feeds.snackbar.errorUpdatingFeed"));
        }
      }
    },
    [selectedPlayer, t]
  );

  const onDragEnd = (result: DropResult) => {
    console.log("--- onDragEnd triggered ---");
    console.log("Drag Result:", result);

    const { source, destination, draggableId } = result;

    if (!destination) {
      console.log("Dropped outside a droppable area.");
      return;
    }

    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      console.log("Dropped in the same position.");
      return;
    }

    const movedItem = allContent.find((c: Content) => c.file_name === draggableId);

    if (!movedItem) {
      console.error("Moved item not found in allContent. This should not happen.");
      return;
    }

    let newFeedContents = Array.from(feedContents);
    let newAvailable = Array.from(available);
    let updatedFeedContentFileNames: string[] = [];

    if (source.droppableId === "feedContents" && destination.droppableId === "feedContents") {
      const [removed] = newFeedContents.splice(source.index, 1);
      newFeedContents.splice(destination.index, 0, removed);
      setFeedContents(newFeedContents);
      updatedFeedContentFileNames = newFeedContents.map((c) => c.file_name);
      console.log("Reordered feedContents locally:", newFeedContents);
    } else if (source.droppableId === "available" && destination.droppableId === "feedContents") {
      newAvailable.splice(source.index, 1);
      newFeedContents.splice(destination.index, 0, movedItem);

      setAvailable(newAvailable);
      setFeedContents(newFeedContents);

      updatedFeedContentFileNames = newFeedContents.map((c) => c.file_name);
      console.log(
        "Moved item from available to feedContents. New feedContents:",
        newFeedContents
      );
      console.log("New available:", newAvailable);
    } else if (source.droppableId === "feedContents" && destination.droppableId === "available") {
      const [removed] = newFeedContents.splice(source.index, 1);
      newAvailable.splice(destination.index, 0, removed);

      setFeedContents(newFeedContents);
      setAvailable(newAvailable);

      updatedFeedContentFileNames = newFeedContents.map((c) => c.file_name);
      console.log(
        "Moved item from feedContents to available. New feedContents:",
        newFeedContents
      );
      console.log("New available:", newAvailable);
    }

    if (
      selectedFeedId &&
      (updatedFeedContentFileNames.length > 0 ||
        (source.droppableId === "feedContents" && newFeedContents.length === 0))
    ) {
      updateFeedContentsState(selectedFeedId, updatedFeedContentFileNames);
    }
  };

  const handleStartFeed = async () => {
    console.log("--- handleStartFeed triggered ---");
    if (selectedPlayer && selectedFeedId && selectedFeedName) {
      try {
        await startFeed(selectedPlayer, selectedFeedId, selectedFeedName);
        setSnackbarMsg(t("feeds.snackbar.feedStarted"));
        console.log("Feed started successfully.");
      } catch (error) {
        console.error("Fehler beim Starten des Feeds:", error);
        setSnackbarMsg(t("feeds.snackbar.errorStartingFeed"));
      }
    } else {
      setSnackbarMsg(t("feeds.snackbar.noFeedSelectedToStart"));
      console.warn(
        "Cannot start feed: Missing selectedPlayer, selectedFeedId, or selectedFeedName."
      );
    }
  };

  const handleCreateFeed = async () => {
    console.log("--- handleCreateFeed triggered ---");
    if (selectedPlayer && feedName.trim()) {
      try {
        await createFeed(selectedPlayer, feedName.trim(), []);
        const fetchedFeeds = await fetchFeeds(selectedPlayer);
        const sortedFeeds = sortFeeds(fetchedFeeds);
        setFeeds(sortedFeeds);
        console.log("Feeds re-fetched and sorted after creation:", sortedFeeds);

        const newFeed = sortedFeeds.find(
          (feed: Feed) => feed.external_name === feedName.trim()
        );
        if (newFeed) {
          setSelectedFeedId(newFeed.id);
          setSelectedFeedName(newFeed.external_name);
          console.log("Selected newly created feed:", newFeed.external_name);
        } else if (sortedFeeds.length > 0) {
          setSelectedFeedId(sortedFeeds[0].id);
          setSelectedFeedName(sortedFeeds[0].external_name);
          console.log(
            "New feed not found by name, selected first feed:",
            sortedFeeds[0].external_name
          );
        } else {
          setSelectedFeedId(null);
          setSelectedFeedName(null);
          console.log("No feeds available after creation.");
        }

        setDialogOpen(false);
        setFeedName("");
        setSnackbarMsg(t("feeds.snackbar.feedCreated"));
      } catch (error) {
        console.error("Fehler beim Erstellen des Feeds:", error);
        setSnackbarMsg(t("feeds.snackbar.errorCreatingFeed"));
      }
    } else {
      setSnackbarMsg(t("feeds.snackbar.enterFeedName"));
      console.warn("Attempted to create feed with no player or empty name.");
    }
  };

  const handleRenameFeed = async () => {
    console.log("--- handleRenameFeed triggered ---");
    if (selectedPlayer && renameFeedId && feedName.trim()) {
      try {
        await renameFeed(selectedPlayer, renameFeedId, feedName.trim());
        const fetchedFeeds = await fetchFeeds(selectedPlayer);
        const sortedFeeds = sortFeeds(fetchedFeeds);
        setFeeds(sortedFeeds);
        console.log("Feeds re-fetched and sorted after rename:", sortedFeeds);

        if (selectedFeedId === renameFeedId) {
          setSelectedFeedName(feedName.trim());
          console.log("Updated selectedFeedName to:", feedName.trim());
        }
        setDialogOpen(false);
        setFeedName("");
        setRenameFeedId(null);
        setSnackbarMsg(t("feeds.snackbar.feedRenamed"));
      } catch (error) {
        console.error("Fehler beim Umbenennen des Feeds:", error);
        setSnackbarMsg(t("feeds.snackbar.errorRenamingFeed"));
      }
    } else {
      setSnackbarMsg(t("feeds.snackbar.enterFeedName"));
      console.warn("Attempted to rename feed with no player, ID, or empty name.");
    }
  };

  const handleDeleteFeed = async (feedId: string) => {
    console.log("--- handleDeleteFeed triggered for ID:", feedId, "---");
    if (selectedPlayer) {
      try {
        const confirmDelete = window.confirm(
          t("feeds.snackbar.confirmDelete")
        );
        if (!confirmDelete) {
          console.log("Delete cancelled by user.");
          setSnackbarMsg(t("feeds.snackbar.deleteCancelled"));
          return;
        }

        await deleteFeed(selectedPlayer, feedId);
        const fetchedFeeds = await fetchFeeds(selectedPlayer);
        const sortedFeeds = sortFeeds(fetchedFeeds);
        setFeeds(sortedFeeds);
        console.log("Feeds re-fetched and sorted after deletion:", sortedFeeds);

        if (selectedFeedId === feedId) {
          setSelectedFeedId(sortedFeeds.length > 0 ? sortedFeeds[0].id : null);
          setSelectedFeedName(
            sortedFeeds.length > 0 ? sortedFeeds[0].external_name : null
          );
          console.log(
            "Deleted selected feed. New selection:",
            sortedFeeds.length > 0 ? sortedFeeds[0].external_name : "none"
          );
        } else if (
          sortedFeeds.length > 0 &&
          !sortedFeeds.some((feed: Feed) => feed.id === selectedFeedId)
        ) {
          setSelectedFeedId(sortedFeeds[0].id);
          setSelectedFeedName(sortedFeeds[0].external_name);
          console.log(
            "Previously selected feed no longer exists. Selecting first feed:",
            sortedFeeds[0].external_name
          );
        }
        setSnackbarMsg(t("feeds.snackbar.feedDeleted"));
      } catch (error) {
        console.error("Fehler beim Löschen des Feeds:", error);
        setSnackbarMsg(t("feeds.snackbar.errorDeletingFeed"));
      }
    }
  };

  const listStyle: React.CSSProperties = {
    minHeight: 250,
    background: "rgb(210, 190, 142)",
    border: "none",
    borderRadius: 8,
    padding: 8,
    transition: "background 0.2s",
  };

  const itemStyle = (isDragging: boolean): React.CSSProperties => ({
    userSelect: "none",
    background: isDragging ? "rgb(220, 200, 152)" : "rgb(200, 180, 132)",
    border: "none",
    borderRadius: 6,
    marginBottom: 8,
    transition: "background 0.18s",
    boxShadow: isDragging ? "0 2px 12px rgba(0, 0, 0, 0.1)" : "none",
    padding: 8,
  });

  useEffect(() => {
    console.log("--- useEffect [dialogType, renameFeedId, feeds] triggered ---");
    if (dialogType === "rename" && renameFeedId) {
      const feed = feeds.find((f: Feed) => f.id === renameFeedId);
      if (feed) {
        setFeedName(feed.external_name);
        console.log("Dialog: Pre-filling rename with:", feed.external_name);
      } else {
        console.warn("Dialog: Feed to rename not found.");
      }
    } else if (dialogType === "create") {
      setFeedName("");
      console.log("Dialog: Clearing feed name for new creation.");
    }
  }, [dialogType, renameFeedId, feeds]);

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 4 }}>
        {t("feeds.title")}
      </Typography>
      {!adminPlayer && (
        <PlayerSelector
          // customerId={user?.customerId || ""} // customerId wird hier nicht mehr direkt verwendet
          userId={user?.id || null} // NEU: userId an PlayerSelector übergeben
          value={selectedPlayer}
          onChange={setSelectedPlayer}
          disabled={false}
        />
      )}
      {adminPlayer && (
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          {t("feeds.player")} <b>{adminPlayer}</b>
        </Typography>
      )}
      {/* Feed-Liste */}
      <Paper sx={{ mt: 1.5, p: 1.5 }}>
        <Box display="flex" alignItems="center" mb={1.5}>
          <Typography variant="h6" flexGrow={1}>
            {t("feeds.feedsSectionTitle")}
          </Typography>
          <Button
            variant="contained"
            onClick={() => {
              setDialogOpen(true);
              setDialogType("create");
              setFeedName("");
            }}
            disabled={!selectedPlayer}
          >
            {t("feeds.createFeedButton")}
          </Button>
        </Box>
        {feeds.length === 0 && selectedPlayer && (
          <Typography sx={{ my: 2, color: "text.secondary" }}>
            {t("feeds.noFeedsAvailable")}
          </Typography>
        )}
        {feeds.map((feed: Feed) => (
          <Box
            key={feed.id}
            sx={{
              background:
                selectedFeedId === feed.id ? "rgb(210, 190, 142)" : "transparent",
              borderRadius: 2,
              px: 2,
              py: 1,
              my: 1,
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
            }}
            onClick={() => {
              console.log("Feed list item clicked:", feed.id, feed.external_name);
              setSelectedFeedId(feed.id);
              setSelectedFeedName(feed.external_name);
            }}
          >
            <Typography variant="body1" flexGrow={1}>
              {feed.external_name}
            </Typography>
            <Button
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setDialogOpen(true);
                setDialogType("rename");
                setFeedName(feed.external_name);
                setRenameFeedId(feed.id);
              }}
            >
              {t("feeds.renameButton")}
            </Button>
            <Button
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteFeed(feed.id);
              }}
            >
              {t("feeds.deleteButton")}
            </Button>
          </Box>
        ))}
      </Paper>
      {/* Drag&Drop-Bereich */}
      {selectedPlayer && selectedFeedId && (
        <Paper sx={{ mt: 1.5, p: 1.5 }}>
          <Box display="flex" alignItems="center" mb={1.5}>
            <Typography variant="h6" flexGrow={1}>
              {t("feeds.editFeedTitle")}{" "}
              <b>{selectedFeedName || t("feeds.noFeedSelected")}</b>{" "}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleStartFeed}
              disabled={!selectedFeedId}
            >
              {t("feeds.startFeedButton")}
            </Button>
          </Box>
          <DragDropContext onDragEnd={onDragEnd}>
            <Box sx={{ display: "flex", gap: 2 }}>
              {/* Verfügbare Inhalte */}
              <Paper sx={{ flex: 1, p: 1.5, minHeight: 300 }}>
                <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
                  {t("feeds.availableContentTitle")}{" "}
                </Typography>
                <Droppable droppableId="available">
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      style={listStyle}
                    >
                      {available.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          {t("feeds.noMoreContentAvailable")}{" "}
                        </Typography>
                      ) : (
                        available.map((item: Content, idx: number) => (
                          <Draggable
                            key={item.file_name}
                            draggableId={item.file_name}
                            index={idx}
                          >
                            {(prov, snap) => (
                              <div
                                ref={prov.innerRef}
                                {...prov.draggableProps}
                                {...prov.dragHandleProps}
                                style={{ ...itemStyle(snap.isDragging), ...prov.draggableProps.style }}
                              >
                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                  {item.content_name}
                                </Typography>
                                {item.mediatype && (
                                  <Typography variant="caption" sx={{ color: "#999" }}>
                                    {item.mediatype}
                                  </Typography>
                                )}
                              </div>
                            )}
                          </Draggable>
                        ))
                      )}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </Paper>
              {/* Feed-Inhalte */}
              <Paper sx={{ flex: 1, p: 1.5, minHeight: 300 }}>
                <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
                  {t("feeds.feedContentTitle")}{" "}
                </Typography>
                <Droppable droppableId="feedContents">
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      style={listStyle}
                    >
                      {feedContents.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          {t("feeds.noContentInFeed")}{" "}
                        </Typography>
                      ) : (
                        feedContents.map((item: Content, idx: number) => (
                          <Draggable
                            key={item.file_name}
                            draggableId={item.file_name}
                            index={idx}
                          >
                            {(prov, snap) => (
                              <div
                                ref={prov.innerRef}
                                {...prov.draggableProps}
                                {...prov.dragHandleProps}
                                style={{ ...itemStyle(snap.isDragging), ...prov.draggableProps.style }}
                              >
                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                  {item.content_name}
                                </Typography>
                                {item.mediatype && (
                                  <Typography variant="caption" sx={{ color: "#999" }}>
                                    {item.mediatype}
                                  </Typography>
                                )}
                              </div>
                            )}
                          </Draggable>
                        ))
                      )}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </Paper>
            </Box>
          </DragDropContext>
        </Paper>
      )}
      {!selectedPlayer && (
        <Typography sx={{ mt: 4, color: "text.secondary" }}>
          {t("feeds.selectPlayerPrompt")}
        </Typography>
      )}
      {selectedPlayer && !selectedFeedId && feeds.length > 0 && (
        <Typography sx={{ mt: 4, color: "text.secondary" }}>
          {t("feeds.selectFeedPrompt")}
        </Typography>
      )}
      {/* Feed anlegen/umbenennen Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>
          {dialogType === "create"
            ? t("feeds.dialogCreateTitle")
            : t("feeds.dialogRenameTitle")}{" "}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label={t("feeds.feedNameLabel")}
            fullWidth
            value={feedName}
            onChange={(e) => setFeedName(e.target.value)}
            sx={{ mt: 2 }}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                if (dialogType === "create") handleCreateFeed();
                else handleRenameFeed();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button
            onClick={dialogType === "create" ? handleCreateFeed : handleRenameFeed}
            disabled={!feedName.trim()}
          >
            {dialogType === "create" ? t("common.create") : t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={!!snackbarMsg}
        autoHideDuration={3000}
        onClose={() => setSnackbarMsg(null)}
        message={snackbarMsg}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      />
    </Box>
  );
};

export default Feeds;